// src/MyProfileMenuItem.tsx

import CancelIcon from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import PersonIcon from '@mui/icons-material/Person'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { useNotify, useStore, useStoreContext, useTranslate, useUserMenu } from 'react-admin'
import QRCode from 'react-qr-code'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { errorMessageOf } from '../utils/api'
import {
  type BusinessAddress,
  CLINIC_COUNTRIES,
  type ClinicCountry,
  composeLegacyAddressLine,
  isValidClinicPhone,
  normalizeClinicCountry,
  resolveStateCode,
  stateOptionsFor,
} from '../utils/clinicAddress'
import { resolveGlucoseUnitPreference } from '../utils/glucoseDefaults'
import { isValidPostalCode, normalizePostalCode } from '../utils/postalCode'

interface VetProfile {
  id: number
  name: string
  email: string
  phone?: string
  clinic_name?: string
  /** Legacy one-line address (street, unit, city, state) — still written for older readers. */
  address?: string
  zip?: string
  /** The structured address the Shop Verification page and the CRM org read. */
  businessAddress?: BusinessAddress
  banner?: string
  profile?: string
  settings?: {
    glucoseUnit?: string
  }
  /** Clinic Team Master/Sub: the master this sub-account belongs to. Null/
      undefined = this user IS a master (or a standalone vet). May arrive as
      a bare id or a populated object depending on the endpoint. */
  masterAccount?: number | { id: number } | null
}

/**
 * What the edit dialog holds. Address is STRUCTURED here (street / city /
 * state / country / postal) and is written to `businessAddress` on save —
 * until 2026-09-11 this dialog wrote only the flat `address` + `zip`, so an
 * account edited here reached Shop Verification with no city/state to submit
 * ("missing required field(s): city, state/province") and the auto-created
 * CRM org had no region and no sales owner.
 */
interface EditForm {
  name: string
  phone: string
  clinic_name: string
  address1: string
  city: string
  state: string
  country: ClinicCountry
  zip: string
  banner: string
  profile: string
}

// The clinic code shown/QR-encoded to pet owners must always be the MASTER
// clinic's id. A sub-account's own id means nothing to the owner app (which
// hides sub-accounts from provider search), so a sub hands out its master's
// id instead; a master (no masterAccount) hands out its own id, unchanged.
// masterAccount may arrive as a bare id or a populated object — mirrors the
// normalization in src/authProvider.ts getIdentity.
export const resolveClinicCodeId = (me: Pick<VetProfile, 'id' | 'masterAccount'>): number =>
  me.masterAccount && typeof me.masterAccount === 'object'
    ? me.masterAccount.id
    : (me.masterAccount ?? me.id)

export const ProfileMenuItem = React.forwardRef<HTMLLIElement>((props, ref) => {
  const translate = useTranslate()
  const notify = useNotify()
  // The glucose-unit CHOICE lives in the app-bar GlucoseUnitMenuButton. This item
  // seeds the store from /me only when this browser has no choice yet (below).
  const [, setGlucoseUnit] = useStore('glucoseUnit', 'mmol/L')
  const store = useStoreContext()
  const [me, setMe] = useState<VetProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [editData, setEditData] = useState<Partial<EditForm>>({})

  const sanitizeEditData = (data: Partial<EditForm>): Partial<EditForm> => ({
    ...data,
    name: data.name?.trim(),
    phone: data.phone?.trim(),
    clinic_name: data.clinic_name?.trim(),
    address1: data.address1?.trim(),
    city: data.city?.trim(),
    state: data.state?.trim(),
    country: normalizeClinicCountry(data.country) ?? 'US',
    zip: normalizePostalCode(data.zip),
    banner: data.banner?.trim(),
    profile: data.profile?.trim(),
  })

  const handleOpen = () => setDialogOpen(true)
  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditMode(false)
    setEditData({})
  }

  const handleEditMode = () => {
    if (me) {
      // Structured first; a legacy flat-address account (address + zip only,
      // the pre-2026-09-11 shape) seeds street and postal from those columns
      // and leaves city/state blank for the vet to fill.
      const ba = me.businessAddress ?? {}
      const country = normalizeClinicCountry(ba.country) ?? 'US'
      setEditData({
        name: me.name,
        clinic_name: me.clinic_name || '',
        phone: me.phone || '',
        address1: ba.address1 || me.address || '',
        city: ba.city || '',
        state: resolveStateCode(ba.state, country) || ba.state || '',
        country,
        zip: ba.postalCode || me.zip || '',
        banner: me.banner || '',
        profile: me.profile || '',
      })
      setEditMode(true)
    }
  }

  const validateForm = () => {
    const sanitizedData = sanitizeEditData(editData)
    const errors: string[] = []

    if (!sanitizedData.name) {
      errors.push(translate('pos.profile.validation_name_required'))
    }

    if (sanitizedData.phone && !isValidClinicPhone(sanitizedData.phone)) {
      errors.push(translate('pos.profile.validation_phone_invalid'))
    }

    if (!isValidPostalCode(sanitizedData.zip)) {
      errors.push(translate('pos.profile.validation_postal_invalid'))
    }

    // State is a two-letter code from the chosen country's list (the
    // dropdown enforces it; this catches a stored value that no longer fits
    // after a country switch, and any programmatic input).
    if (sanitizedData.state && !resolveStateCode(sanitizedData.state, sanitizedData.country)) {
      errors.push(translate('pos.profile.validation_state_invalid'))
    }

    return errors
  }

  const handleSave = async () => {
    if (!me) return

    // Validate form
    const validationErrors = validateForm()
    if (validationErrors.length > 0) {
      notify(validationErrors.join('. '), { type: 'error' })
      return
    }

    setSaving(true)
    try {
      const d = sanitizeEditData(editData)
      const businessAddress: BusinessAddress = {
        // Keep the sub-fields this dialog does not edit (address2, phone) so a
        // group PATCH cannot blank them.
        ...(me.businessAddress ?? {}),
        address1: d.address1 ?? '',
        city: d.city ?? '',
        state: resolveStateCode(d.state, d.country),
        postalCode: d.zip ?? '',
        country: d.country ?? 'US',
      }
      const payload = {
        name: d.name,
        phone: d.phone,
        clinic_name: d.clinic_name,
        banner: d.banner,
        profile: d.profile,
        businessAddress,
        // Legacy readers (the registration→CRM mirror, ops' ZIP extractor) still
        // consume the one-line form; keep it in step with the structured group.
        address: composeLegacyAddressLine(businessAddress),
        zip: d.zip,
      }
      const token = sessionStorage.getItem('token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      const response = await deadlineFetch(`/api/users/${me.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        const updatedData = await response.json()
        setMe(updatedData.doc)
        setEditMode(false)
        setEditData({})
        notify(translate('pos.profile.profile_updated'), { type: 'success' })
      } else {
        // Payload validation errors arrive as {errors:[{message}]}, not
        // {message} — reading only .message silently discarded the server's
        // actual reason (P71-6). errorMessageOf also swallows a non-JSON body
        // instead of throwing, falling back to the translated generic message.
        const message = await errorMessageOf(
          response,
          translate('pos.profile.profile_update_failed'),
        )
        notify(message, { type: 'error' })
      }
    } catch (error) {
      notify(translate('pos.profile.profile_update_failed'), { type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleFieldChange = (field: keyof EditForm, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }))
  }

  // Switching country invalidates a state that is not in the new list — a US
  // code left under CA would fail validation with no visible reason.
  const handleCountryChange = (value: string) => {
    const country = normalizeClinicCountry(value) ?? 'US'
    setEditData((prev) => ({
      ...prev,
      country,
      state: resolveStateCode(prev.state, country),
    }))
  }

  useEffect(() => {
    const token = sessionStorage.getItem('token')
    const headers: HeadersInit = { Accept: 'application/json' }
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    deadlineFetch('/api/users/me', { headers })
      .then((res) => res.json())
      .then((data: any) => {
        const user = data.user
        setMe(user)
        // Seed the unit ONLY when this browser has no saved choice -- the same
        // rule as login (authProvider.applyAuthResult): this browser's choice
        // wins, the account's cloud unit is only the fallback seed
        // (storeConfig.ts, 2026-06-13). This item mounts every time the user
        // menu opens, so seeding unconditionally made each menu-open override
        // the vet's choice with the cloud's (or the geographic default).
        // Ask the STORE, not raw localStorage: with storage blocked the RA
        // store falls back to memory, and the vet's choice lives there.
        const hasLocalChoice = store.getItem('glucoseUnit') != null
        if (!hasLocalChoice) {
          setGlucoseUnit(resolveGlucoseUnitPreference(user?.settings?.glucoseUnit, user))
        }
      })
      .catch(() => setMe(null))
      .finally(() => setLoading(false))
  }, [setGlucoseUnit, store])
  useEffect(() => {
    return () => {
      // Ensure dialog is closed when the menu unmounts (e.g., on logout)
      setDialogOpen(false)
    }
  }, [])
  // onClose will hide the menu when clicked
  // @ts-expect-error: useUserMenu does not expose onClose in types
  const { onClose } = useUserMenu()

  // Don't render early - conditionally render content instead
  return (
    <>
      {!loading && me && (
        <MenuItem
          ref={ref}
          onClick={(e) => {
            e.stopPropagation() // prevent menu from closing
            handleOpen() // open the dialog
          }}
          {...props}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {/* Label kept short ("My Profile") — the user's name is already shown
              on the UserMenu trigger button, so repeating it here was redundant
              and a very long name (e.g. test data) overflowed/clipped the menu.
              noWrap + maxWidth guards against overflow regardless of label. */}
          <ListItemText
            primary={translate('pos.profile.my_profile')}
            primaryTypographyProps={{ noWrap: true }}
            sx={{ maxWidth: 240 }}
          />
        </MenuItem>
      )}
      {me && (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editMode ? translate('pos.profile.edit_profile') : translate('pos.profile.my_profile')}
          </DialogTitle>
          <DialogContent dividers>
            {editMode ? (
              <Box sx={{ '& .MuiTextField-root': { mb: 2 } }}>
                <TextField
                  fullWidth
                  label={translate('pos.profile.name')}
                  value={editData.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  required
                />
                <TextField
                  fullWidth
                  label={translate('pos.profile.field_clinic_name')}
                  value={editData.clinic_name || ''}
                  onChange={(e) => handleFieldChange('clinic_name', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={translate('pos.profile.phone')}
                  value={editData.phone || ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={translate('pos.profile.field_address')}
                  value={editData.address1 || ''}
                  onChange={(e) => handleFieldChange('address1', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={translate('pos.profile.field_city')}
                  value={editData.city || ''}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                />
                <TextField
                  select
                  fullWidth
                  label={translate('pos.profile.field_country')}
                  value={editData.country || 'US'}
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  {CLINIC_COUNTRIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {translate(c === 'US' ? 'pos.profile.country_us' : 'pos.profile.country_ca')}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label={translate('pos.profile.field_state')}
                  value={editData.state || ''}
                  onChange={(e) => handleFieldChange('state', e.target.value)}
                >
                  <MenuItem value="">—</MenuItem>
                  {stateOptionsFor(editData.country).map((s) => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  label={translate('pos.profile.field_postal_code')}
                  value={editData.zip || ''}
                  onChange={(e) => handleFieldChange('zip', e.target.value)}
                />
                <TextField
                  fullWidth
                  label={translate('pos.profile.field_short_description')}
                  value={editData.banner || ''}
                  onChange={(e) => handleFieldChange('banner', e.target.value)}
                  placeholder={translate('pos.profile.placeholder_short_description')}
                />
                <TextField
                  fullWidth
                  label={translate('pos.profile.field_full_profile')}
                  value={editData.profile || ''}
                  onChange={(e) => handleFieldChange('profile', e.target.value)}
                  multiline
                  rows={4}
                  placeholder={translate('pos.profile.placeholder_full_profile')}
                />
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1">
                  {translate('pos.profile.name')}: {me.name}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.id')}: {me.id}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.email')}: {me.email}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.phone')}: {me.phone || translate('pos.profile.not_set')}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.clinic')}:{' '}
                  {me.clinic_name || translate('pos.profile.not_set')}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.field_address')}:{' '}
                  {composeLegacyAddressLine(me.businessAddress ?? {}) ||
                    me.address ||
                    translate('pos.profile.not_set')}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.field_postal_code')}:{' '}
                  {me.zip || translate('pos.profile.not_set')}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.field_short_description')}:{' '}
                  {me.banner || translate('pos.profile.not_set')}
                </Typography>
                <Typography variant="subtitle1">
                  {translate('pos.profile.field_full_profile')}:{' '}
                  {me.profile || translate('pos.profile.not_set')}
                </Typography>

                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  {translate('pos.profile.qr_code_clinic_label', { id: resolveClinicCodeId(me) })}
                </Typography>
                <QRCode value={String(resolveClinicCodeId(me))} size={128} />
              </>
            )}
          </DialogContent>
          <DialogActions>
            {editMode ? (
              <>
                <Button
                  onClick={() => setEditMode(false)}
                  startIcon={<CancelIcon />}
                  disabled={saving}
                >
                  {translate('pos.profile.cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? translate('pos.profile.saving') : translate('pos.profile.save')}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => window.print()}>{translate('pos.profile.print')}</Button>
                <Button onClick={handleEditMode} startIcon={<EditIcon />} variant="outlined">
                  {translate('pos.profile.edit_profile')}
                </Button>
                <Button
                  onClick={() => {
                    handleCloseDialog()
                    onClose()
                  }}
                  autoFocus
                >
                  {translate('pos.profile.ok')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
    </>
  )
})
